// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Constants
import { DEFAULT_DECIBEL_VALUE_ESTERMAN } from "src/constants/enums";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import useExamReportStats from "src/hooks/reports/useExamReportStats";

// Utils
import {
  getReportEyeText,
  getTutorialCompletionText,
} from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";

const useVFEstermanColumns = () => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { eyeType, duration, examReport } = useContext(ReportSectionContext);

  const {
    fixationLossMethod,
    fixationLossPercentage,
    falsePositivePercentage,
    falseNegative,
  } = useExamReportStats(examReport);

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(
            examReport.timing.start,
            clinicInfo?.timezone
          )
        : { date: "N/A", time: "N/A" },
    [examReport?.timing?.start, clinicInfo?.timezone]
  );

  const vfEstermanColumns = useMemo(() => {
    const firstColumn = [
      {
        title: t("Eye"),
        value: getReportEyeText(eyeType, t),
        isBlueText: true,
      },
      {
        title: t("Fixation Method"),
        value: clinicInfo?.settings?.MTOFixationMethod
          ? t("Off")
          : fixationLossMethod,
        isBlueText: true,
      },
      {
        title: t("Binocular Fixation Loss"),
        value: t("False"),
        isBlueText: true,
      },
      {
        title: t("False Positives"),
        value: falsePositivePercentage,
        isBlueText: true,
      },
      {
        title: t("False Negatives"),
        value: falseNegative,
        isBlueText: true,
      },
    ];

    if (!clinicInfo?.settings?.MTOFixationMethod) {
      firstColumn.splice(3, 0, {
        title: t("Fixation Loss"),
        value: fixationLossPercentage,
        isBlueText: true,
      });
    }

    const secondColumn = [
      {
        title: t("Speed Type"),
        value: examReport?.exam?.speedType
          ? t(examReport?.exam?.speedType)
          : t("Normal"),
        isBlueText: true,
      },
      {
        title: t("Stimulus"),
        value: `${examReport?.exam?.stimulusSize}, ${t("White")}`,
        isBlueText: true,
      },
      {
        title: t("Stimulus Intensity"),
        value: `${examReport?.exam?.decibelValue ?? DEFAULT_DECIBEL_VALUE_ESTERMAN} dB`,
        isBlueText: true,
      },
      {
        title: t("Test Mode"),
        value: t("Single Intensity"),
        isBlueText: true,
      },
      {
        title: t("Fovea Measurement"),
        value:
          typeof examReport?.postProcessing?.foveaDb === "number"
            ? `${examReport?.postProcessing?.foveaDb} dB`
            : t("Off"),
        isBlueText: true,
      },
      {
        title: t("Tutorial"),
        value: getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ),
        isBlueText: false,
      },
    ];

    const thirdColumn = [
      { title: t("Date"), value: localDateAndTimeWithZone?.date },
      { title: t("Time_hour"), value: localDateAndTimeWithZone?.time },
      {
        title: t("Duration"),
        value: duration,
        isBlueText: true,
      },
    ];

    return { firstColumn, secondColumn, thirdColumn };
  }, [
    clinicInfo?.settings?.MTOFixationMethod,
    examReport?.postProcessing?.foveaDb,
    examReport?.postProcessing?.tutorialCompletionState,
    examReport?.exam?.speedType,
    examReport?.exam?.decibelValue,
    examReport?.exam?.stimulusSize,
    localDateAndTimeWithZone,
    eyeType,
    duration,
    fixationLossMethod,
    fixationLossPercentage,
    falsePositivePercentage,
    falseNegative,
    t,
  ]);

  return { vfEstermanColumns };
};

export default useVFEstermanColumns;
